import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { WarningOutlined } from '@material-ui/icons';
import { formatCurrency } from '@roc/ui/utils';
import { insertIf } from '@roc/feature-utils';
import { useStore } from '@roc/client-portal-shared/hooks';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  outerRow: {
    padding: theme.spacing(1.5, 0),
  },
  content: {
    margin: '10px',
  },
  table: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    margin: '0px 10px',
  },
  tableRow: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  footer: {
    paddingTop: theme.spacing(1),
  },
  footerRow: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  label: {
    fontSize: '1rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
  boldLabel: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  description: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  value: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(3),
  },
  grandTotalLabel: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  grandTotalValue: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(3),
  },
}));

interface LiquidityBreakdownProps {
  loanId: number;
}

interface BreakdownRow {
  label: string;
  description?: string;
  value: number;
  variant: 'total' | 'detail' | 'grandTotal';
  group: 'header' | 'table' | 'footer';
}

export const LiquidityEstimateDisclaimer = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: 16,
      borderRadius: 8,
      border: '1px solid #F59E0B',
      backgroundColor: '#FFFBEB',
      color: '#92400E',
      fontSize: '0.875rem',
      fontWeight: 700,
    }}
  >
    <WarningOutlined
      style={{ color: '#92400E', fontSize: '1.1rem', flexShrink: 0 }}
    />
    Please note that this is only an estimate based on numbers at the time of
    underwriting, this may update to different numbers once we have a closing
    date and the final HUD is approved
  </div>
);

export const LiquidityBreakdown = observer(
  ({ loanId }: LiquidityBreakdownProps) => {
    const classes = useStyles();
    const { loanStore } = useStore();
    const loanDetails = loanStore.loanDetails;

    useEffect(() => {
      if (!loanDetails) {
        loanStore.fetchLoanDetails(loanId);
      }
    }, [loanId]);

    const liquidity = loanDetails?.liquidityRequiredCalculationValues;
    const cashProceeds = loanDetails?.cashProceeds ?? 0;
    const estimatedCashToClose = loanDetails?.estimatedCashToClose ?? 0;
    const showFullBreakdown = estimatedCashToClose >= 0;

    const headerRows: BreakdownRow[] = showFullBreakdown
      ? [
        {
          label: 'Estimated Cash To Close',
          value: estimatedCashToClose,
          variant: 'total',
          group: 'header',
        },
      ]
      : [];

    const tableRows: BreakdownRow[] = showFullBreakdown
      ? [
        {
          label: 'Down Payment / Cash Out',
          description: 'Down payment or cashout at closing',
          value: liquidity?.delta ?? 0,
          variant: 'detail' as const,
          group: 'table' as const,
        },
        {
          label: 'Origination Fee',
          description: 'Fees collected by Originator',
          value: liquidity?.originationFees ?? 0,
          variant: 'detail',
          group: 'table',
        },
        {
          label: 'Total Fees',
          description: 'Other fees including processing & underwriting fees',
          value: loanDetails?.totalFees ?? 0,
          variant: 'detail',
          group: 'table',
        },
        {
          label: 'Estimated Closing Cost',
          description: 'Third-party closing costs',
          value: liquidity?.estimatedClosingCosts ?? 0,
          variant: 'detail',
          group: 'table',
        },
        {
          label: 'PITIA at Closing',
          description: 'Escrow collected at closing',
          value: loanDetails?.escrow ?? 0,
          variant: 'detail',
          group: 'table',
        },
        {
          label: 'Corelogic Fee',
          description: 'Third-party property data report',
          value: liquidity?.coreLogixFee ?? 0,
          variant: 'detail',
          group: 'table',
        },
        {
          label: 'Legal Fee',
          description: 'Attorney and document preparation',
          value: liquidity?.legalFee ?? 0,
          variant: 'detail',
          group: 'table',
        },
      ]
      : [];

    const continuedPaymentRow: BreakdownRow = {
      label: 'PITIA for Continued Payment',
      description: '3 or 6 months of PITIA based on loan purpose',
      value: loanDetails?.continuedPayments ?? 0,
      variant: 'total',
      group: 'footer',
    };

    const continuedPaymentTableRows: BreakdownRow[] = [
      {
        label: 'Principal and Interest Payment',
        description: 'Monthly amount',
        value: liquidity?.piReservesAtClose ?? 0,
        variant: 'detail',
        group: 'table',
      },
      {
        label: 'Tax Escrow',
        description: 'Monthly amount',
        value: liquidity?.taxesCollectedAtClose ?? 0,
        variant: 'detail',
        group: 'table',
      },
      {
        label: 'Insurance Escrow',
        description: 'Monthly amount',
        value: liquidity?.insuranceCollectedAtClose ?? 0,
        variant: 'detail',
        group: 'table',
      },
    ];

    const footerRows: BreakdownRow[] = [
      ...insertIf(showFullBreakdown && cashProceeds !== 0, [
        {
          label: '50% of Cash Proceeds',
          description: 'Half of net cash-out proceeds applied toward liquidity',
          value: cashProceeds,
          variant: 'detail' as const,
          group: 'footer' as const,
        },
      ]),
      {
        label: 'Total Liquidity Required',
        value: loanDetails?.liquidityNeededForClose ?? 0,
        variant: 'grandTotal',
        group: 'footer',
      },
    ];

    return (
      <Box paddingBottom={'50px'} className={classes.content}>
        <Box style={{ marginBottom: 20 }}>
          <LiquidityEstimateDisclaimer />
        </Box>
        {headerRows.map(row => (
          <div key={row.label} className={`${classes.row} ${classes.outerRow}`}>
            <Typography className={classes.boldLabel}>{row.label}</Typography>
            <Typography className={classes.value}>
              {formatCurrency(row.value)}
            </Typography>
          </div>
        ))}

        {tableRows.length > 0 && (
          <Box className={classes.table}>
            {tableRows.map(row => (
              <div
                key={row.label}
                className={`${classes.row} ${classes.tableRow}`}
              >
                <Box>
                  <Typography className={classes.label}>{row.label}</Typography>
                  {row.description && (
                    <Typography className={classes.description}>
                      {row.description}
                    </Typography>
                  )}
                </Box>
                <Typography className={classes.value}>
                  {formatCurrency(row.value)}
                </Typography>
              </div>
            ))}
          </Box>
        )}

        <div className={`${classes.row} ${classes.outerRow}`}>
          <Box>
            <Typography className={classes.boldLabel}>
              {continuedPaymentRow.label}
            </Typography>
            {continuedPaymentRow.description && (
              <Typography className={classes.description}>
                {continuedPaymentRow.description}
              </Typography>
            )}
          </Box>
          <Typography className={classes.value}>
            {formatCurrency(continuedPaymentRow.value)}
          </Typography>
        </div>

        <Box className={classes.table}>
          {continuedPaymentTableRows.map(row => (
            <div
              key={row.label}
              className={`${classes.row} ${classes.tableRow}`}
            >
              <Box>
                <Typography className={classes.label}>{row.label}</Typography>
                {row.description && (
                  <Typography className={classes.description}>
                    {row.description}
                  </Typography>
                )}
              </Box>
              <Typography className={classes.value}>
                {formatCurrency(row.value)}
              </Typography>
            </div>
          ))}
        </Box>

        <Box className={classes.footer}>
          {footerRows.map(row => {
            const labelClass =
              row.variant === 'grandTotal'
                ? classes.grandTotalLabel
                : row.variant === 'detail'
                  ? classes.label
                  : classes.boldLabel;
            const valueClass =
              row.variant === 'grandTotal'
                ? classes.grandTotalValue
                : classes.value;

            return (
              <div
                key={row.label}
                className={`${classes.row} ${classes.outerRow} ${classes.footerRow}`}
              >
                <Box>
                  <Typography className={labelClass}>{row.label}</Typography>
                  {row.description && (
                    <Typography className={classes.description}>
                      {row.description}
                    </Typography>
                  )}
                </Box>
                <Typography className={valueClass}>
                  {formatCurrency(row.value)}
                </Typography>
              </div>
            );
          })}
        </Box>
      </Box>
    );
  }
);
